import {
  CloudDownloadOutlined,
  ContentCopyOutlined,
  DeleteOutline,
  EditOutlined,
} from '@mui/icons-material';
import { type FileRowAction, FileRowActions } from 'components/ui';
import { useZoom } from 'context/ZoomContext';
import { FIRM_DOCUMENT_FILE_ACTIONS_LABELS } from './FirmDocumentFileActions.constants';
import { FIRM_DOCUMENT_FILE_ACTIONS_TEST_IDS } from './FirmDocumentFileActions.testIds';
import type { FirmDocumentFileActionsProps } from './FirmDocumentFileActions.types';

const FirmDocumentFileActions = ({
  actionInProgress = false,
  canCopy = false,
  canDelete = true,
  canDownload = true,
  canRename = true,
  documentReference,
  fileName,
  isDownloadInProgress = false,
  onCopy,
  onDelete,
  onDownload,
  onRename,
}: FirmDocumentFileActionsProps) => {
  const { zoomLevel } = useZoom();
  const documentReferenceId = documentReference.document_reference_id;
  const isDisabled = actionInProgress || !documentReference.file.id;
  const isDownloadDisabled = isDisabled || !canDownload || isDownloadInProgress;
  const actions: FileRowAction[] = [
    {
      ariaLabel: FIRM_DOCUMENT_FILE_ACTIONS_LABELS.download,
      disabled: isDownloadDisabled,
      icon: <CloudDownloadOutlined fontSize="inherit" />,
      id: 'download',
      isLoading: isDownloadInProgress,
      onClick: event => {
        event.stopPropagation();
        onDownload(documentReference);
      },
      testId: FIRM_DOCUMENT_FILE_ACTIONS_TEST_IDS.downloadAction(documentReferenceId),
      tooltipLabel: FIRM_DOCUMENT_FILE_ACTIONS_LABELS.download,
    },
    ...(onCopy ? [{
      ariaLabel: FIRM_DOCUMENT_FILE_ACTIONS_LABELS.copy,
      disabled: isDisabled || !canCopy,
      icon: <ContentCopyOutlined fontSize="inherit" />,
      id: 'copy',
      onClick: event => {
        event.stopPropagation();
        onCopy(documentReference);
      },
      testId: FIRM_DOCUMENT_FILE_ACTIONS_TEST_IDS.copyAction(documentReferenceId),
      tooltipLabel: FIRM_DOCUMENT_FILE_ACTIONS_LABELS.copy,
    } satisfies FileRowAction] : []),
    {
      ariaLabel: FIRM_DOCUMENT_FILE_ACTIONS_LABELS.rename,
      disabled: isDisabled || !canRename,
      icon: <EditOutlined fontSize="inherit" />,
      id: 'rename',
      onClick: event => {
        event.stopPropagation();
        onRename(documentReference);
      },
      testId: FIRM_DOCUMENT_FILE_ACTIONS_TEST_IDS.renameAction(documentReferenceId),
      tooltipLabel: FIRM_DOCUMENT_FILE_ACTIONS_LABELS.rename,
    },
    {
      ariaLabel: FIRM_DOCUMENT_FILE_ACTIONS_LABELS.delete,
      disabled: isDisabled || !canDelete,
      icon: <DeleteOutline fontSize="inherit" />,
      id: 'delete',
      onClick: event => {
        event.stopPropagation();
        onDelete(documentReference);
      },
      testId: FIRM_DOCUMENT_FILE_ACTIONS_TEST_IDS.deleteAction(documentReferenceId),
      tone: 'danger',
      tooltipLabel: FIRM_DOCUMENT_FILE_ACTIONS_LABELS.delete,
    },
  ];

  return (
    <FileRowActions
      actions={actions}
      ariaLabel={FIRM_DOCUMENT_FILE_ACTIONS_LABELS.trigger(fileName)}
      testId={FIRM_DOCUMENT_FILE_ACTIONS_TEST_IDS.root(documentReferenceId)}
      zoomLevel={zoomLevel}
    />
  );
};

export default FirmDocumentFileActions;
